import { yupResolver } from '@hookform/resolvers/yup';
import { Add, Check, Send } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Paper, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import * as yup from 'yup';
import { FormInput } from '../../../main-app/components/form/FormInput';
import { useSafeNavigation } from '../../../main-app/hooks/useSafeNavigation';
import { langKeys } from '../../../main-app/localization/formErrors';
import { getConfig } from '../../../main-app/utils/config';
import { notify } from '../../../main-app/utils/notification';
import { toCentesimal, toDateString } from '../../../main-app/utils/transform';
import { validators } from '../../../main-app/utils/validators';
import { LeadInvoiceInput, useCreateLeadMutation } from '../../api/gql';
import { ConnectedInvoice } from '../../components/ConnectedInvoice';
import { PageLayout } from '../../layout/PageLayout';
import { useLeadInvoiceStore } from '../../store/leadStore';

const { registerUrl } = getConfig();

export const LeadPage = () => {
  const { leadInvoiceState } = useLeadInvoiceStore();

  const { safeNavigate } = useSafeNavigation();

  const formMethods = useForm({
    defaultValues: leadInvoiceState ?? {},
  });

  const { control, handleSubmit } = useForm<LeadFormType>({
    resolver: yupResolver(leadFormValidationSchema),
  });

  const { mutateAsync, isPending, data } = useCreateLeadMutation();

  const onSubmit = async (data: LeadFormType) => {
    if (!leadInvoiceState) {
      safeNavigate('/', { replace: true });
      return;
    }

    try {
      await mutateAsync({
        input: {
          email: data.email,
          invoice: {
            ...(leadInvoiceState as LeadInvoiceInput),
            amount: toCentesimal(leadInvoiceState.amount),
            amountWithoutVat: leadInvoiceState.amountWithoutVat
              ? toCentesimal(leadInvoiceState.amountWithoutVat)
              : null,
            amountVat: leadInvoiceState.amountVat ? toCentesimal(leadInvoiceState.amountVat) : null,
            issueDate: leadInvoiceState.issueDate ? toDateString(leadInvoiceState.issueDate.toString()) : null,
            dueDate: leadInvoiceState.dueDate ? toDateString(leadInvoiceState.dueDate.toString()) : null,
            taxPoint: leadInvoiceState.taxPoint ? toDateString(leadInvoiceState.taxPoint.toString()) : null,
            items: leadInvoiceState.items.map((item) => ({
              ...item,
              pricePerUnit: toCentesimal(item.pricePerUnit),
              priceSum: toCentesimal(item.priceSum),
              priceWithoutVat: item.priceWithoutVat ? toCentesimal(item.priceWithoutVat) : undefined,
              vatRate:
                item.vatRate !== undefined && item.vatRate !== null
                  ? typeof item.vatRate === 'string'
                    ? item.vatRate
                    : item.vatRate.toString()
                  : undefined,
            })),
          },
        },
      });
    } catch (e) {
      console.error(e);
      notify.negative({
        message: 'Nepodařilo se odeslat z e-mail. Zkuste to prosím znovu.',
      });
    }
  };

  return (
    <PageLayout>
      <Typography variant={'h1'} textAlign={'center'}>
        {data ? 'Děkujeme' : 'Chybí poslední krok'}
      </Typography>
      <Box
        position={'relative'}
        display={'flex'}
        mt={{ xs: 2, sm: 4, md: 6 }}
        justifyContent={'center'}
        alignItems={'center'}
      >
        <Box
          flex={1}
          sx={{
            display: { xs: 'none', lg: 'block' },
            zIndex: -1,
            position: 'absolute',
            opacity: 0.6,
            top: 0,
            right: 0,
            transform: 'scale(50%) translateY(-50%) rotate(25deg)',
          }}
        >
          <Paper
            sx={{
              width: '21cm',
              height: '29.7cm',
              p: { xs: 2, sm: 4, md: 6 },
            }}
          >
            <FormProvider {...formMethods}>
              <ConnectedInvoice readonly={true} />
            </FormProvider>
          </Paper>
        </Box>
        <Paper
          sx={{
            p: { xs: 2, sm: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            alignItems: 'center',
          }}
        >
          {data ? (
            <>
              <Check color={'success'} sx={{ fontSize: 62 }} />
              <Typography variant={'subtitle1'}>Vaši fakturu naleznete v e-mailu.</Typography>
              <Typography variant={'subtitle2'}>
                Nebo v aplikaci Upomínej po{' '}
                <Link to={registerUrl} target={'_blank'}>
                  registraci
                </Link>
                .
              </Typography>
              <Button component={Link} to={'/'} startIcon={<Add />}>
                Založit další fakturu
              </Button>
            </>
          ) : (
            <>
              <Typography variant={'h2'}>Zadejte e-mail, na který zašleme vaši fakturu.</Typography>
              <Box
                component={'form'}
                width={'100%'}
                justifyContent={'center'}
                display={'flex'}
                flexDirection={'column'}
                alignItems={'center'}
                onSubmit={handleSubmit(onSubmit)}
              >
                <FormInput control={control} name={'email'} label={'E-mail*'} />
                <LoadingButton
                  loading={isPending}
                  size={'large'}
                  endIcon={<Send />}
                  variant={'contained'}
                  type={'submit'}
                  sx={{ mt: 4 }}
                >
                  Odeslat
                </LoadingButton>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </PageLayout>
  );
};

type LeadFormType = {
  email: string;
};

const leadFormValidationSchema = yup.object().shape({
  email: yup.string().required().matches(validators.email, {
    message: langKeys.invalid,
    excludeEmptyString: true,
  }),
});
